import { useEffect, useState } from 'react'
import { useUser, useClerk } from '@clerk/clerk-react'
import { useSearchParams } from 'react-router-dom'
import { Check, Loader2 } from 'lucide-react'
import { MatrixRain } from '@/components/matrix-rain'
import { useApi } from '@/hooks/use-api'

/**
 * Real Stripe checkout page — replaces the old "Early Access Waitlist" wall.
 *
 * The user reaches this AFTER onboarding (onboarding-first flow) and only if
 * they aren't yet entitled (no live subscription / not comped). It shows the
 * customer-facing plans with the price that will ACTUALLY be charged (fetched
 * from /api/billing/plans, which reads the live Stripe price config) so the
 * displayed amount can never differ from the Stripe charge. "Subscribe" hits
 * the regular /api/billing/checkout endpoint and redirects to Stripe Checkout.
 */

type PlanOption = {
  plan: 'starter' | 'pro'
  name: string
  amount_cents: number
  compare_at_cents?: number | null
  beta_pricing?: boolean
  currency: string
  interval: 'month'
  features: string[]
}

function formatPrice(cents: number): string {
  const dollars = cents / 100
  return `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`
}

export default function BillingPage() {
  const { user } = useUser()
  const { signOut } = useClerk()
  const apiFetch = useApi()
  const [searchParams] = useSearchParams()

  const [plans, setPlans] = useState<PlanOption[] | null>(null)
  const [selected, setSelected] = useState<'starter' | 'pro'>(() => {
    const qp = searchParams.get('plan')
    if (qp === 'starter' || qp === 'pro') return qp
    const stored = localStorage.getItem('earlyAccessPlan')
    return stored === 'starter' ? 'starter' : 'pro'
  })
  const [loadingCheckout, setLoadingCheckout] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const userEmail = user?.primaryEmailAddress?.emailAddress

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const res = await apiFetch<{ plans: PlanOption[] }>('/api/billing/plans')
      if (cancelled) return
      if (res.success && res.data) {
        setPlans(res.data.plans)
      } else {
        setError(res.error ?? 'Could not load plans. Please refresh.')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiFetch])

  async function handleSubscribe() {
    setLoadingCheckout(true)
    setError(null)
    // Promo captured from ?promo=CODE on signup / first load (auth-provider,
    // signup page). The backend pre-applies it to the Checkout Session when
    // it matches an active Stripe promotion code, otherwise ignores it.
    let promo: string | null = null
    try {
      promo = localStorage.getItem('dca_promo')
    } catch {
      // localStorage disabled — proceed without a promo
    }
    const res = await apiFetch<{ url: string }>('/api/billing/checkout', {
      method: 'POST',
      body: { plan: selected, email: userEmail, ...(promo ? { promo } : {}) },
    })
    if (!res.success || !res.data?.url) {
      setLoadingCheckout(false)
      setError(res.error ?? 'Something went wrong starting checkout. Please try again.')
      return
    }
    // Hand off to Stripe-hosted Checkout.
    window.location.href = res.data.url
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <MatrixRain />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-grid-subtle opacity-30" />

      <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-12">
        <div className="mx-auto w-full max-w-3xl text-center">
          <h1 className="mb-2 text-3xl font-bold tracking-tight">Choose your plan</h1>
          <p className="mb-8 text-sm text-muted-foreground">
            Your setup is ready. Activate a plan to launch your AI outreach.
          </p>

          {searchParams.get('checkout') === 'cancelled' && (
            <p className="mb-6 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-600">
              Checkout was cancelled — you haven't been charged. Pick a plan to try again.
            </p>
          )}

          {!plans && !error && (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {plans && (
            <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {plans.map((p) => {
                const isSelected = selected === p.plan
                return (
                  <button
                    key={p.plan}
                    type="button"
                    onClick={() => setSelected(p.plan)}
                    className={`rounded-xl border p-6 text-left transition ${
                      isSelected
                        ? 'border-cobalt-bright ring-2 ring-primary/40 bg-card'
                        : 'border-border bg-card/60 hover:border-primary/50'
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <h2 className="font-semibold">{p.name}</h2>
                      {p.plan === 'pro' && (
                        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-cobalt-bright">
                          Most popular
                        </span>
                      )}
                    </div>
                    <div className="mb-1 flex items-baseline gap-1.5">
                      {p.compare_at_cents && p.compare_at_cents > p.amount_cents ? (
                        <span className="text-lg font-semibold text-muted-foreground line-through">
                          {formatPrice(p.compare_at_cents)}
                        </span>
                      ) : null}
                      <span className="text-3xl font-bold">{formatPrice(p.amount_cents)}</span>
                      <span className="text-sm text-muted-foreground">/{p.interval}</span>
                    </div>
                    {p.beta_pricing ? (
                      <p className="mb-4 text-xs font-medium uppercase tracking-wide text-cobalt-bright">
                        Beta pricing
                      </p>
                    ) : (
                      <div className="mb-4" />
                    )}
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-cobalt-bright" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                )
              })}
            </div>
          )}

          <button
            onClick={() => void handleSubscribe()}
            disabled={loadingCheckout || !plans}
            className="w-full max-w-sm rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loadingCheckout ? 'Redirecting to secure checkout…' : 'Subscribe & continue'}
          </button>

          {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

          <p className="mt-4 text-xs text-muted-foreground">
            Secure payment by Stripe. Cancel anytime from Settings.
          </p>

          <p className="mt-6 text-sm text-muted-foreground">
            Signed in as <span className="font-medium text-foreground">{userEmail}</span>
          </p>
          <button
            onClick={() => void signOut()}
            className="mt-2 text-sm text-muted-foreground underline hover:text-foreground"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
